import { VideoEditorLogger } from '../../utility/VideoEditorLogger';
import { EditorChangeType } from '../types';

import type { VideoEditor } from '../VideoEditor';
import type { Command } from './Command';

/**
 * Command for setting the timeline duration.
 * Handles undo/redo functionality by capturing the previous duration state.
 */
export class SetTimelineDurationCommand implements Command {
  private editor: VideoEditor;

  getName(): string {
    return 'SetTimelineDurationCommand';
  }

  private newDuration: number | null;
  private previousDuration: number | null;
  private executed: boolean = false;
  private _shouldCreateUndoRedoEntry: boolean = true;

  constructor(
    editor: VideoEditor,
    duration: number | null,
    shouldCreateUndoRedoEntry: boolean = true,
  ) {
    this.editor = editor;
    this.newDuration = duration;
    this._shouldCreateUndoRedoEntry = shouldCreateUndoRedoEntry;
    // Capture the current duration for undo
    this.previousDuration = this.editor.timeline.duration;
  }

  shouldCreateUndoRedoEntry(): boolean {
    return this._shouldCreateUndoRedoEntry;
  }

  execute(): boolean {
    try {
      VideoEditorLogger.logCommand(this, 'start', {
        newDuration: this.newDuration,
        previousDuration: this.previousDuration,
      });

      // Set the new duration on the timeline
      this.editor.timeline.setDuration(this.newDuration);
      this.executed = true;

      // Mark the editor as dirty to trigger synchronization
      this.editor.markDirty('set_timeline_duration_command');

      // Notify observers of the timeline change
      this.editor.notifyObservers(EditorChangeType.TIMELINE_CHANGED, {
        type: 'duration_changed',
        newDuration: this.newDuration,
        previousDuration: this.previousDuration,
      });

      VideoEditorLogger.logCommand(this, 'complete', {
        newDuration: this.newDuration,
      });

      return true;
    } catch (error) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: error instanceof Error ? error.message : String(error),
      });
      return false;
    }
  }

  undo(): boolean {
    if (!this.executed) return false;

    try {
      VideoEditorLogger.logCommand(this, 'start', {
        operation: 'undo',
        restoringDuration: this.previousDuration,
        currentDuration: this.newDuration,
      });

      // Restore the previous duration
      this.editor.timeline.setDuration(this.previousDuration);
      this.executed = false;

      // Mark the editor as dirty to trigger synchronization
      this.editor.markDirty('set_timeline_duration_command_undo');

      // Notify observers of the timeline change
      this.editor.notifyObservers(EditorChangeType.TIMELINE_CHANGED, {
        type: 'duration_changed',
        newDuration: this.previousDuration,
        previousDuration: this.newDuration,
      });

      VideoEditorLogger.logCommand(this, 'complete', {
        operation: 'undo',
        restoredDuration: this.previousDuration,
      });

      return true;
    } catch (error) {
      VideoEditorLogger.logCommand(this, 'error', {
        operation: 'undo',
        error: error instanceof Error ? error.message : String(error),
      });
      return false;
    }
  }

  getDescription(): string {
    return `Set timeline duration to ${this.newDuration} seconds`;
  }
}
